// 防抖 Hook
import { ref } from 'vue';

/**
 * 创建防抖函数
 * @param fn 要防抖的函数
 * @param delay 延迟时间（毫秒），默认 500ms
 * @returns 防抖后的函数
 */
export function useDebounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 500
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return function (this: any, ...args: Parameters<T>) {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      fn.apply(this, args);
      timer = null;
    }, delay);
  };
}

/**
 * 创建带锁的防抖函数（防止快速点击）
 * 适用于异步操作，在操作完成前会阻止重复调用
 * @param fn 要防抖的异步函数
 * @param delay 延迟时间（毫秒），默认 300ms
 * @returns 防抖后的函数
 */
export function useDebounceLock<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number = 300
): (...args: Parameters<T>) => Promise<void> {
  const isLocked = ref(false);

  return async function (this: any, ...args: Parameters<T>) {
    if (isLocked.value) {
      console.log('🔒 操作进行中，请稍候...');
      return;
    }

    isLocked.value = true;

    try {
      await fn.apply(this, args);
    } catch (error) {
      console.error('操作失败:', error);
      throw error;
    } finally {
      // 延迟解锁，防止快速点击
      setTimeout(() => {
        isLocked.value = false;
      }, delay);
    }
  };
}

/**
 * 节流函数
 * @param fn 要节流的函数
 * @param delay 间隔时间（毫秒），默认 500ms
 * @returns 节流后的函数
 */
export function useThrottle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 500
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let isThrottled = false;

  return function (this: any, ...args: Parameters<T>) {
    if (isThrottled) {
      return;
    }

    isThrottled = true;
    fn.apply(this, args);

    timer = setTimeout(() => {
      isThrottled = false;
      timer = null;
    }, delay);
  };
}
